import { TrashSimple } from "@phosphor-icons/react"
import { useState } from "react"
import { Button, IconButton } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { trackEvent } from "~/hooks/useTrackingService"
import { api } from "~/utils/api"
import { useDeleteAdjustment } from "~/hooks/useAdjustments"

type Props = {
  name: string
  groupId: string
  identifiers: {
    variantIds: string[]
    warehouseId: string
  }[]
  open?: boolean
  onClose?: () => void
}

export const DeleteAdjustmentDialog = ({
  name,
  groupId,
  identifiers,
  open: controlledOpen,
  onClose,
}: Props) => {
  const { data: warehouses } = api.warehouse.get.useQuery()

  const isControlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isControlled ? controlledOpen : internalOpen

  const deleteAdjustment = useDeleteAdjustment()

  const variantIds = [...new Set(identifiers.flatMap((id) => id.variantIds))]
  const closeDialog = () => {
    if (isControlled) {
      onClose?.()
    } else {
      setInternalOpen(false)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      if (!isControlled) setInternalOpen(true)
    } else {
      closeDialog()
    }
  }

  const handleDeleteSuccess = () => {
    trackEvent("Adjustment Deleted")
    closeDialog()
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger requires="edit" asChild>
          <IconButton variant="neutral" size="sm">
            <TrashSimple />
          </IconButton>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete adjustment for all variants?</DialogTitle>
        </DialogHeader>
        <div className="text-sm">
          This will permanently delete &quot;{name}&quot;
          {identifiers.length > 1
            ? " across all warehouses"
            : ` on warehouse "${warehouses?.find((w: any) => w.id === identifiers[0]?.warehouseId)?.name}"`}{" "}
          for{" "}
          <span className="font-medium">
            all {variantIds.length} variant{variantIds.length !== 1 ? "s" : ""}
          </span>{" "}
          in this adjustment, not only the ones currently selected. This cannot be undone and will
          affect your forecast.
        </div>
        <DialogFooter className="flex justify-between">
          {isControlled ? (
            <Button variant="transparent" onClick={closeDialog}>
              Cancel
            </Button>
          ) : (
            <DialogClose>
              <Button asChild variant="transparent">
                Cancel
              </Button>
            </DialogClose>
          )}
          <Button
            variant="danger"
            requires="edit"
            loading={deleteAdjustment.isPending}
            onClick={() => {
              const warehouseIds = identifiers.map((id) => id.warehouseId)
              deleteAdjustment.mutate(
                { variantIds, groupId, warehouseIds },
                { onSuccess: handleDeleteSuccess },
              )
            }}
          >
            Delete adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
